import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, Container, Typography, FormControlLabel, Switch,
  Backdrop, CircularProgress, Card, Stack, Avatar, Grid,
  Paper, Divider, Chip, IconButton
} from '@mui/material';
import {
  ViewList as ViewListIcon,
  Summarize as SummarizeIcon,
  AccountBalanceWallet as WalletIcon,
  Inventory as InventoryIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import { IonContent, IonPage } from '@ionic/react';
import { requestID, baseUrl, config } from '../../../common/utils';
import StockDashboard from '../../../components/StockDashboard';
import { StockItem } from '../../../models/stocks';

// Extended StockItem with valuation
interface ExtendedStockItem extends StockItem {
  totalValuation: number; // Quantity × Price
}

const InventoryReportingValuation: React.FC = () => {
  const [stockList, setStockList] = useState<ExtendedStockItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<'detailed' | 'master'>('detailed');
  const [totalValuation, setTotalValuation] = useState(0);
  const [productCount, setProductCount] = useState(0);
  const [totalQuantity, setTotalQuantity] = useState(0);

  // Calculate total valuation for all products
  const calculateTotalValuation = (stocks: ExtendedStockItem[]): number => {
    return stocks.reduce((total, stock) => {
      return total + (stock.totalValuation || 0);
    }, 0);
  };

  // Calculate total quantity
  const calculateTotalQuantity = (stocks: ExtendedStockItem[]): number => {
    return stocks.reduce((total, stock) => {
      return total + (stock.quantity || 0);
    }, 0);
  };

  // Transform API data to include individual product valuation
  const transformStockData = (apiData: any[]): ExtendedStockItem[] => {
    return apiData.map(item => {
      // Extract quantity and price
      const quantity = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.price) || 0;
      const totalValuation = quantity * price;

      return {
        ...item,
        quantity,
        price,
        totalValuation
      };
    });
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const endpoint = viewMode === 'master'
        ? `${baseUrl()}Geacloud_Inventorys/getMasterStockLevels`
        : `${baseUrl()}Geacloud_Inventorys/getStockLevels`;

      // Fetch stock data
      const stockRes = await axios.post(endpoint, formData, config);

      if (stockRes.data.status === true) {
        // Transform the data to include valuations
        const transformedData = transformStockData(stockRes.data.data);
        setStockList(transformedData);
        setProductCount(transformedData.length);

        // Calculate totals from transformed data
        const calculatedTotal = calculateTotalValuation(transformedData);
        const calculatedQuantity = calculateTotalQuantity(transformedData);
        setTotalValuation(calculatedTotal);
        setTotalQuantity(calculatedQuantity);
      } else {
        // Fallback to API valuation if available
        try {
          const valRes = await axios.post(`${baseUrl()}Geacloud_Inventorys/getInventoryStats`, formData, config);
          if (valRes.data.status === true) {
            setTotalValuation(valRes.data.data.total_valuation);
          }
        } catch (apiError) {
          console.error("Error fetching API valuation:", apiError);
        }
      }

    } catch (error) {
      console.error("Error fetching stock data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [viewMode]);

  const handleRefresh = () => {
    fetchData();
  };

  return (
    <IonPage>
      <IonContent>
        <Box sx={{
          backgroundColor: '#f8fafc',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {/* HEADER */}
          <Box sx={{
            background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
            px: { xs: 2, md: 3 },
            py: 4,
            boxShadow: '0 4px 20px rgba(59, 130, 246, 0.15)',
            position: 'sticky',
            top: 0,
            zIndex: 1000
          }}>
            <Container maxWidth="xl">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <Avatar sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      width: 48,
                      height: 48,
                      backdropFilter: 'blur(10px)'
                    }}>
                      <InventoryIcon sx={{ color: 'white' }} />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" fontWeight={700} sx={{ color: 'white', fontSize: { xs: '1.5rem', md: '2rem' } }}>
                        Inventory Valuation
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mt: 0.5 }}>
                        Comprehensive stock management and valuation insights
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                  {/* REFRESH BUTTON */}
                  <IconButton
                    onClick={handleRefresh}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.25)',
                        transform: 'rotate(180deg)',
                        transition: 'transform 0.5s ease'
                      }
                    }}
                  >
                    <RefreshIcon />
                  </IconButton>

                  {/* VIEW MODE SWITCH */}
                  <Paper sx={{
                    bgcolor: 'rgba(255,255,255,0.1)',
                    p: 1,
                    borderRadius: 3,
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.2)'
                  }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={viewMode === 'master'}
                          onChange={(e) => setViewMode(e.target.checked ? 'master' : 'detailed')}
                          color="default"
                          sx={{
                            '& .MuiSwitch-track': {
                              bgcolor: 'rgba(255,255,255,0.3)'
                            }
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                          {viewMode === 'master' ? <SummarizeIcon fontSize="small" /> : <ViewListIcon fontSize="small" />}
                          {viewMode === 'master' ? 'Consolidated View' : 'Detailed View'}
                        </Typography>
                      }
                      sx={{ mr: 0, ml: 1 }}
                    />
                  </Paper>
                </Stack>
              </Box>
            </Container>
          </Box>

          {/* STATS CARDS */}
          <Box sx={{ px: { xs: 2, md: 3 }, py: 4 }}>
            <Container maxWidth="xl">
              <Grid container spacing={3}>
                {/* TOTAL VALUATION CARD */}
                <Grid item xs={12} md={4}>
                  <Card sx={{
                    height: '100%',
                    background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                    color: 'white',
                    p: 3,
                    borderRadius: 3,
                    boxShadow: '0 10px 25px rgba(16, 185, 129, 0.3)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                          <WalletIcon />
                        </Avatar>
                        <TrendingUpIcon sx={{ fontSize: 40, opacity: 0.3 }} />
                      </Box>
                      <Typography variant="h3" fontWeight={700} sx={{ mb: 1 }}>
                        ₦{Number(totalValuation).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Total Inventory Valuation
                      </Typography>
                      <Divider sx={{ my: 2, bgcolor: 'rgba(255,255,255,0.2)' }} />
                      <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Calculated from {productCount} products
                      </Typography>
                    </Box>
                  </Card>
                </Grid>

                {/* TOTAL PRODUCTS CARD */}
                <Grid item xs={12} md={4}>
                  <Card sx={{
                    height: '100%',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
                    color: 'white',
                    p: 3,
                    borderRadius: 3,
                    boxShadow: '0 10px 25px rgba(59, 130, 246, 0.3)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                          <CategoryIcon />
                        </Avatar>
                        <ViewListIcon sx={{ fontSize: 40, opacity: 0.3 }} />
                      </Box>
                      <Typography variant="h3" fontWeight={700} sx={{ mb: 1 }}>
                        {productCount}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Total Products
                      </Typography>
                      <Divider sx={{ my: 2, bgcolor: 'rgba(255,255,255,0.2)' }} />
                      <Chip
                        label={viewMode === 'master' ? 'Consolidated' : 'Detailed'}
                        size="small"
                        sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                      />
                    </Box>
                  </Card>
                </Grid>

                {/* TOTAL QUANTITY CARD */}
                <Grid item xs={12} md={4}>
                  <Card sx={{
                    height: '100%',
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)',
                    color: 'white',
                    p: 3,
                    borderRadius: 3,
                    boxShadow: '0 10px 25px rgba(139, 92, 246, 0.3)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                          <InventoryIcon />
                        </Avatar>
                        <TrendingUpIcon sx={{ fontSize: 40, opacity: 0.3 }} />
                      </Box>
                      <Typography variant="h3" fontWeight={700} sx={{ mb: 1 }}>
                        {totalQuantity.toLocaleString()}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Total Units in Stock
                      </Typography>
                      <Divider sx={{ my: 2, bgcolor: 'rgba(255,255,255,0.2)' }} />
                      <Typography variant="caption">
                        Across all products
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            </Container>
          </Box>

          {/* DASHBOARD CONTENT */}
          <Box sx={{ flex: 1, px: { xs: 2, md: 3 }, pb: 4 }}>
            <Container maxWidth="xl" sx={{ height: '100%' }}>
              <Paper sx={{
                height: '100%',
                borderRadius: 3,
                overflow: 'hidden',
                boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
                border: '1px solid #e2e8f0'
              }}>
                <Box sx={{ p: 3, bgcolor: '#f1f5f9', borderBottom: '1px solid #e2e8f0' }}>
                  <Typography variant="h6" fontWeight={600} color="#1e293b">
                    Stock Details
                  </Typography>
                  <Typography variant="body2" color="#64748b">
                    View and manage inventory items with real-time valuation
                  </Typography>
                </Box>
                <StockDashboard
                  stocks={stockList}
                  loading={loading}
                  onRefresh={fetchData}
                  onTransfer={(item) => {
                    // Optional: You can remove this or keep it for logging
                    console.log("Product selected:", item.stock_name);
                  }}
                />
              </Paper>
            </Container>
          </Box>

          {/* LOADING BACKDROP */}
          <Backdrop
            sx={{
              color: '#fff',
              zIndex: (theme) => theme.zIndex.drawer + 1,
              backdropFilter: 'blur(4px)'
            }}
            open={loading}
          >
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress color="inherit" size={60} thickness={4} />
              <Typography variant="h6" sx={{ mt: 2, fontWeight: 600 }}>
                Loading Inventory Data...
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                Fetching latest stock levels and valuations
              </Typography>
            </Box>
          </Backdrop>
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default InventoryReportingValuation;